import { observer } from 'mobx-react'
import styles from './BuyPopup.module.less'
import { Popup } from 'antd-mobile'
import classNames from 'classnames'
import wechatpay from '@/assets/images/wechat-pay.png'
import alipay from '@/assets/images/ali-pay.png'
import close from '@/assets/images/close.png'
import { useEffect, useRef, useState } from 'react'
import { api } from '@/helpers/api'
import { commonUtils } from '@/helpers/commonUtils'
import { userService } from '@/services/userService/userService'
import { payService } from '@/services/payService'
import { CheckCircleFill } from 'antd-mobile-icons'
import { goodsUtils } from '@/helpers/goodsUtils'

export interface BuyPopupProps {
  goodsId?: string
  goodsType?: DataModels.goods.GoodsType
  visible: boolean
  onClose?: () => void
  onDownload?: () => void
  onPaySuccess?: () => void
}

let isPaying = false
const BuyPopup = observer((props: BuyPopupProps) => {
  const countdownTimerRef = useRef<number | null>(null)
  const couponRequestIdRef = useRef(0)
  const [checked, setChecked] = useState(1)
  const [chargeChannel, setChargeChannel] = useState<'wx_pub_qr' | 'alipay_qr'>('wx_pub_qr')
  const [priceInfo, setPriceInfo] = useState<DataModels.goods.CourseMaterialPriceInfo[]>([])
  
  // 新增优惠券相关状态
  const [coupons, setCoupons] = useState<DataModels.Coupon.UseCouponItem[]>([])
  const [selectedCoupon, setSelectedCoupon] = useState<DataModels.Coupon.UseCouponItem | null>(null)
  const [showCouponPanel, setShowCouponPanel] = useState(false)
  const [currentTimestamp, setCurrentTimestamp] = useState(Date.now())

  useEffect(() => {
    const initPlanData = async () => {
      if (!props.goodsId || !props.goodsType) return
      const { data } = await api.goods.getCourseMaterialPriceInfo(props.goodsId, props.goodsType)
      setPriceInfo(data)
      // setChecked(data.length)
    }
    initPlanData()
  }, [props.goodsId, props.goodsType])

  useEffect(() => {
    if (!props.visible) {
      return undefined
    }

    setCurrentTimestamp(Date.now())

    countdownTimerRef.current = window.setInterval(() => {
      setCurrentTimestamp(Date.now())
    }, 1000)

    return () => {
      if (countdownTimerRef.current) {
        window.clearInterval(countdownTimerRef.current)
        countdownTimerRef.current = null
      }
    }
  }, [props.visible])

  // 获取可用优惠券
  useEffect(() => {
    const requestId = ++couponRequestIdRef.current
    setCoupons([])
    setSelectedCoupon(null)
    setShowCouponPanel(false)

    const currentGoods = priceInfo[checked - 1]
    if (!props.visible || !props.goodsId || !currentGoods?.goodsId) {
      return undefined
    }

    const fetchCoupons = async (): Promise<void> => {
      try {
        const packageCode = goodsUtils.getPricePlanPackageCode(currentGoods)
        const { data } = await api.order.getAvaliableCoupons(
          currentGoods.goodsId,
          packageCode
        )

        if (requestId !== couponRequestIdRef.current) {
          return
        }

        setCoupons(data.list || [])
        if (data.list?.length > 0) {
          setSelectedCoupon(data.list[0])
        }
      } catch (error) {
        if (requestId === couponRequestIdRef.current) {
          console.error('获取优惠券失败:', error)
        }
      }
    }

    fetchCoupons()

    return () => {
      if (requestId === couponRequestIdRef.current) {
        couponRequestIdRef.current += 1
      }
    }
  }, [checked, priceInfo, props.goodsId, props.goodsType, props.visible])

  const parseCurrency = (price?: number) => {
    if (typeof price === 'number') {
      return commonUtils.parseCurrency(price)
    }
    return '-'
  }

  const isSingleCourse = (item?: DataModels.goods.CourseMaterialPriceInfo) => {
    return item?.goodsType === 1
  }

  const hasLimitedPromotion = (item?: DataModels.goods.CourseMaterialPriceInfo) => {
    if (!item || !isSingleCourse(item) || !item.priceLine) {
      return false
    }

    return true
  }

  const getDisplayPrice = (item?: DataModels.goods.CourseMaterialPriceInfo) => {
    if (!item) {
      return 0
    }

    return item.goodsPrice
  }

  const getOriginalPrice = (item?: DataModels.goods.CourseMaterialPriceInfo) => {
    if (!item || !hasLimitedPromotion(item)) {
      return undefined
    }

    return item.priceLine
  }

  const getCountdownSeconds = (item?: DataModels.goods.CourseMaterialPriceInfo) => {
    if (!item?.promotionEndTime) {
      return 0
    }

    return Math.max(
      0,
      Math.floor((new Date(item.promotionEndTime).getTime() - currentTimestamp) / 1000)
    )
  }

  const formatCountdown = (totalSeconds: number) => {
    const days = Math.floor(totalSeconds / (24 * 3600))
    const hours = Math.floor(totalSeconds / 3600)
    const remainHours = Math.floor((totalSeconds % (24 * 3600)) / 3600)
    const minutes = Math.floor((totalSeconds % 3600) / 60)
    const seconds = totalSeconds % 60

    if (days > 0) {
      return `${days}天${String(remainHours).padStart(2, '0')}时${String(minutes).padStart(
        2,
        '0'
      )}分${String(seconds).padStart(2, '0')}秒`
    }

    return `${String(hours).padStart(2, '0')}时${String(minutes).padStart(2, '0')}分${String(
      seconds
    ).padStart(2, '0')}秒`
  }

  // 计算优惠后的价格
  const calculateFinalPrice = () => {
    const originalPrice = getDisplayPrice(priceInfo[checked - 1])
    if (selectedCoupon) {
      return Math.max(0, originalPrice  - selectedCoupon.rule.reductAmount )
    }
    return originalPrice 
  }

  // 处理优惠券选择
  const handleCouponSelect = (coupon: DataModels.Coupon.UseCouponItem) => {
    setSelectedCoupon(coupon)
    setShowCouponPanel(false)
  }

  useEffect(() => {
    const currentItem = priceInfo[checked - 1]
    if (!props.visible || !countdownTimerRef.current || !currentItem?.promotionEndTime) {
      return
    }

    const countdown = Math.max(
      0,
      Math.floor((new Date(currentItem.promotionEndTime).getTime() - currentTimestamp) / 1000)
    )

    if (countdown === 0) {
      window.clearInterval(countdownTimerRef.current)
      countdownTimerRef.current = null
    }
  }, [checked, currentTimestamp, priceInfo, props.visible])

  const doPay = async () => {
   
    if (isPaying) {
      return
    }
    userService.postUserActionRecord({
      module: '移动端官网',
      function: '付费弹窗',
      position: '付费弹窗点击购买按钮',
      action: '点击',
      element: '按钮',
    })
    isPaying = true

    const currentGoods = priceInfo[checked - 1]
    const packageCode = goodsUtils.getPricePlanPackageCode(currentGoods)
    commonUtils.replaceState(
      packageCode
        ? { add: { packageCode } }
        : { delete: ['packageCode'] }
    )
    await payService.createH5Pay({
      goodsId: currentGoods?.goodsId,
      goodsType: currentGoods?.goodsType,
      chargeChannel,
      userCouponId: selectedCoupon?.id,
      packageCode,
      onPaySuccess: () => {
        props.onClose?.()
        props.onPaySuccess?.()
      },
    })
     selectedCoupon && setSelectedCoupon(null) // 切换到单课件时清除已选优惠券
    isPaying = false
  }

  const onClose = () => {
    couponRequestIdRef.current += 1
    setCoupons([])
    setSelectedCoupon(null)
    setShowCouponPanel(false)
    props.onClose?.()
  }
  const isChineseSuperVip =
    priceInfo[checked - 1]?.packageCode === 'bilu_and_course_package_vip'

  return (
    <Popup
      visible={props.visible}
      onMaskClick={onClose}
      bodyStyle={{
        borderTopLeftRadius: '20px',
        borderTopRightRadius: '20px',
      }}
    >
      <div className={styles.buyPopup}>
        <div className={styles.buyPopupHeader}>
          <div>下载课程及教案</div>
          <div onClick={onClose} className={styles.buyPopupHeaderClose}>
            <img src={close} draggable={false} />
          </div>
        </div>
        <div className={styles.buyPopupContent}>
          <div className={styles.buyPopupUser}>
            <div className={styles.infoBox}>
              <div className={styles.infoBoxLabel}>账号：</div>
              <div className={styles.infoBoxVal}>{userService.userId}</div>
            </div>
          </div>
          <div className={styles.buyPopupCourse}>
            {priceInfo.map((item, index) => {
              const isPackage = goodsUtils.isPackagePricePlan(item)
              const isSinglePlan = !isPackage && isSingleCourse(item)
              const packageDisplay = goodsUtils.getPackagePlanDisplayInfo(item)
              const normalPlanIndex = priceInfo
                .slice(0, index)
                .filter((plan) => !goodsUtils.isPackagePricePlan(plan)).length
              const normalPlanClass = isPackage
                ? undefined
                : [undefined, styles.volume, styles.grade, styles.schoolSection][normalPlanIndex]

              return (
                <div
                  key={`${goodsUtils.getPricePlanKey(item)}:${index}`}
                  className={classNames(styles.goodsBox, normalPlanClass, {
                    [styles.checked]: checked === index + 1,
                    [styles.packagePlan]: isPackage,
                  })}
                  onClick={() => {
                    couponRequestIdRef.current += 1
                    setChecked(index + 1)
                    setCoupons([])
                    setSelectedCoupon(null)
                    setShowCouponPanel(false)
                  }}
                >
                  {isPackage && <div className={styles.packageRecommend}>推荐</div>}
                  <div className={styles.goodsBoxTitle}>
                    {isPackage ? packageDisplay.title : isSinglePlan ? '单个课件' : item.goodsName}
                  </div>
                  <div className={styles.goodsBoxGift}>
                    {isPackage ? (
                      <>
                        <span></span>
                        {packageDisplay.subtitle && <span>{packageDisplay.subtitle}</span>}
                      </>
                    ) : isSinglePlan ? (
                      <span>
                        <img src={commonUtils.getStaticFile('pay/1.png')} alt='' />
                        赠送教学设计（部分科目包含）
                      </span>
                    ) : (
                      <>
                        <span>
                          <img src={commonUtils.getStaticFile('pay/2.png')} alt='' />
                          包含 <span className={styles.highlight}>{item.couresCount}</span> 套课件
                        </span>
                        <span>
                          <img src={commonUtils.getStaticFile('pay/2.png')} alt='' />
                          赠送教学设计及配套素材
                        </span>
                      </>
                    )}
                  </div>
                  {hasLimitedPromotion(item) ? (
                    <div className={classNames(styles.goodsBoxPrice, styles.limitedPriceBox)}>
                      <div className={styles.limitedPriceLine}>
                        <div className={styles.limitedPriceTag}>限时价</div>
                        <div className={styles.limitedPriceValue}>
                          <span className={styles.unit}>￥</span>
                          {parseCurrency(getDisplayPrice(item))}
                        </div>
                      </div>
                      <div className={styles.originalPriceText}>
                        原价 ¥{parseCurrency(getOriginalPrice(item))}
                      </div>
                    </div>
                  ) : (
                    <div className={styles.goodsBoxPrice}>
                      <span className={styles.unit}>￥</span>
                      {parseCurrency(item.goodsPrice)}
                      {isPackage && item.priceLine && (
                        <div className={styles.packageOriginalPrice}>
                          原价 ¥{parseCurrency(item.priceLine)}
                        </div>
                      )}
                      {!isPackage && !isSinglePlan && item.onePrice && (
                        <div className={styles.onePrice}>
                          单课件仅需
                          <span className={styles.highlight}>{parseCurrency(item.onePrice)}</span>元
                        </div>
                      )}
                    </div>
                  )}
                  {hasLimitedPromotion(item) && (
                    <div className={styles.countdownText}>
                      {getCountdownSeconds(item) > 0
                        ? `距结束${formatCountdown(getCountdownSeconds(item))}`
                        : ''}
                    </div>
                  )}
                  <div className={styles.virtualGoodsNotice}>
                    您将购买的商品为虚拟内容服务，购买后不支持退订、转让、退换，请斟酌确认。
                  </div>
                </div>
              )
            })}
          </div>

          {/* 优惠券模块 - 折叠卡片形式 */}
          {coupons.length > 0 && (
            <div className={styles.couponSection}>
              <div
                className={styles.couponCard}
                onClick={() => setShowCouponPanel(!showCouponPanel)}
              >
                {selectedCoupon && (
                  <div className={styles.selectedCornerMark}>
                    ✓ 已选
                  </div>
                )}
                <div className={styles.couponCardContent}>
                  {selectedCoupon ? (
                    // 已选状态 - 电商卡片样式
                    <div className={styles.selectedCouponContent}>
                      <div className={styles.couponLeft}>
                        <div className={styles.couponAmountLarge}>
                          <span className={styles.yuan}>¥</span>
                          {parseCurrency(selectedCoupon.rule.reductAmount)}
                        </div>
                        <div className={styles.couponConditionSmall}>
                          满{parseCurrency(selectedCoupon.rule.minOrderPrice)}可用
                        </div>
                      </div>
                      <div className={styles.couponMiddle}>
                        <div className={styles.couponItemName}>{selectedCoupon.name}</div>
                        <div className={styles.couponItemDesc}>
                          {selectedCoupon.type}
                        </div>
                      </div>
                      {/* <div className={styles.couponRight}>
                        <div
                          className={styles.removeButton}
                          onClick={(e) => {
                            e.stopPropagation()
                            handleRemoveCoupon()
                          }}
                        >
                          移除
                        </div>
                      </div> */}
                    </div>
                  ) : (
                    // 未选状态
                    <div className={styles.unselectedCouponContent}>
                      <div className={styles.couponInfo}>
                        <div className={styles.couponMainText}>
                          可用优惠券 ({coupons.length}张)
                        </div>
                        <div className={styles.couponSubText}>
                          最高省¥{parseCurrency(Math.max(...coupons.map(c => c.rule.reductAmount)))}
                        </div>
                      </div>
                    </div>
                  )}
                </div>
                <div className={classNames(styles.couponArrow, {
                  [styles.couponArrowExpanded]: showCouponPanel
                })}>
                  ›
                </div>
              </div>

              {/* 优惠券选择面板 - 直接内嵌展开 */}
              {showCouponPanel && (
                <div className={styles.couponListWrapper}>
                  {coupons.map((coupon) => (
                    <div
                      key={coupon.id}
                      className={classNames(styles.couponItem, {
                        [styles.couponItemSelected]: selectedCoupon?.id === coupon.id,
                      })}
                      onClick={() => handleCouponSelect(coupon)}
                    >
                      <div className={styles.couponLeft}>
                        <div className={styles.couponAmountLarge}>
                          <span className={styles.yuan}>¥</span>
                          {parseCurrency(coupon.rule.reductAmount)}
                        </div>
                        <div className={styles.couponConditionSmall}>
                          满{parseCurrency(coupon.rule.minOrderPrice)}可用
                        </div>
                      </div>
                      <div className={styles.couponMiddle}>
                        <div className={styles.couponItemName}>{coupon.name}</div>
                        <div className={styles.couponItemDesc}>
                          {coupon.type}
                        </div>
                        <div className={styles.couponValidity}>
                          有效期：{new Date(coupon.startTime).toLocaleDateString('zh-CN')} 至{' '}
                          {new Date(coupon.endTime).toLocaleDateString('zh-CN')}
                        </div>
                      </div>
                      <div className={styles.couponRight}>
                        {selectedCoupon?.id === coupon.id ? (
                          <div className={styles.selectedMark}>✓ </div>
                        ) : (
                          <div className={styles.useButton}>使用</div>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          <div className={classNames(styles.infoBox, styles.payItemBox)}>
            <div
              className={classNames(styles.payTypeItem)}
              onClick={() => {setChargeChannel('wx_pub_qr');selectedCoupon && setSelectedCoupon(null)}}
            >
              <div>
                <img className={styles.icon} src={wechatpay} />
                微信支付
              </div>
              {chargeChannel === 'wx_pub_qr' ? (
                <CheckCircleFill className={styles.checkIcon} color='#FF7006' />
              ) : (
                <div className={styles.checkIcon1} />
              )}
            </div>
            {!commonUtils.isWechat() && (
              <div
                className={classNames(styles.payTypeItem)}
                onClick={() => {setChargeChannel('alipay_qr');selectedCoupon && setSelectedCoupon(null)}}
              >
                <div>
                  <img className={styles.icon} src={alipay} />
                  支付宝支付
                </div>
                {chargeChannel === 'alipay_qr' ? (
                  <CheckCircleFill className={styles.checkIcon} color='#FF7006' />
                ) : (
                  <div className={styles.checkIcon1} />
                )}
              </div>
              )}
          </div>
          {isChineseSuperVip && (
            <div className={styles.packageRedeemTip}>
              <span>重要提示</span>
              作文年会员需添加客服领取兑换码
            </div>
          )}
        </div>
        <div className={styles.buyPopupFooter}>
          <div className={styles.buyPopupFooterLeft}>
            <div className={styles.payBarLeftLabel}>应付：</div>
            <div className={styles.payBarLeftVal}>
              <span>￥</span>
              {parseCurrency(calculateFinalPrice())}
              {getOriginalPrice(priceInfo[checked - 1]) && (
                <span className={styles.originalPrice}>
                   ¥{parseCurrency(getOriginalPrice(priceInfo[checked - 1]))}
                </span>
              )}
               {selectedCoupon && priceInfo[checked - 1]?.goodsType!==1 && (
                <span className={styles.originalPrice}>
                  ¥{parseCurrency(priceInfo[checked - 1]?.goodsPrice)}
                </span>
              )}
            </div>
          </div>
          {/* {selectedCoupon && (
            <div className={styles.saveTag}>
              省¥{parseCurrency(selectedCoupon.rule.reductAmount)}
            </div>
          )} */}
          <div
            className={styles.payBtn}
            onClick={() => {
              // 购买
              doPay()
            }}
          >
            购买
          </div>
        </div>
      </div>
    </Popup>
  )
})

export default BuyPopup
